import { BellOff, BellRing, Info, Smartphone } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { PushDevice, PushSettings } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { useDisablePush } from '../hooks/use-disable-push';
import { useEnablePush } from '../hooks/use-enable-push';
import { useSendTestPush } from '../hooks/use-send-test-push';
import { notificationPermission, PushPermissionError, pushSupport } from '../lib/push-browser';

export interface ThisDeviceCardProps {
  settings: PushSettings;
  device: PushDevice | null;
  syncing: boolean;
}

/** The on/off switch for this browser, the reasons it may be unavailable, and a test push. */
export function ThisDeviceCard({ settings, device, syncing }: ThisDeviceCardProps) {
  const { t } = useTranslation('push');
  const switchId = useId();
  const [support] = useState(pushSupport);
  const [permission, setPermission] = useState(notificationPermission);
  const enable = useEnablePush(settings.publicKey);
  const disable = useDisablePush(settings.publicKey);
  const test = useSendTestPush();
  const on = device !== null;
  const busy = syncing || enable.isPending || disable.isPending;

  const toggle = (checked: boolean) => {
    test.reset();
    if (checked) {
      enable.mutate(undefined, { onSettled: () => setPermission(notificationPermission()) });
    } else {
      disable.mutate(device);
    }
  };

  const enableError =
    enable.error && !(enable.error instanceof PushPermissionError) ? enable.error : null;

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <Smartphone className="text-muted-foreground size-4" aria-hidden />
          <CardTitle>{t('thisDevice.title')}</CardTitle>
          {support === 'supported' && permission !== 'denied' ? (
            <Badge variant={on ? 'default' : 'secondary'} data-testid="push-status">
              {on ? <BellRing aria-hidden /> : <BellOff aria-hidden />}
              {on ? t('thisDevice.on') : t('thisDevice.off')}
            </Badge>
          ) : null}
        </div>
        <CardDescription>{t('thisDevice.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 text-sm">
        {settings.delivery === 'log' ? (
          <Alert data-testid="push-log-mode">
            <Info aria-hidden />
            <AlertTitle>{t('thisDevice.logTitle')}</AlertTitle>
            <AlertDescription>{t('thisDevice.log')}</AlertDescription>
          </Alert>
        ) : null}

        {support === 'needs-install' ? (
          <Alert>
            <Smartphone aria-hidden />
            <AlertTitle>{t('thisDevice.needsInstallTitle')}</AlertTitle>
            <AlertDescription>{t('thisDevice.needsInstall')}</AlertDescription>
          </Alert>
        ) : support === 'unsupported' ? (
          <p className="text-muted-foreground">{t('thisDevice.unsupported')}</p>
        ) : permission === 'denied' ? (
          <Alert variant="destructive" role="alert">
            <BellOff aria-hidden />
            <AlertTitle>{t('thisDevice.deniedTitle')}</AlertTitle>
            <AlertDescription>{t('thisDevice.denied')}</AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="flex items-center justify-between gap-4">
              <Label htmlFor={switchId}>{t('thisDevice.toggle')}</Label>
              <Switch id={switchId} checked={on} disabled={busy} onCheckedChange={toggle} />
            </div>
            {enableError ? (
              <p className="text-destructive" role="alert">
                {t('thisDevice.failed', { message: enableError.message })}
              </p>
            ) : null}
            {on ? (
              <div className="flex flex-col items-start gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => test.mutate()}
                  disabled={test.isPending}
                >
                  {t('thisDevice.test')}
                </Button>
                {test.data ? (
                  <p className="text-muted-foreground" data-testid="push-test-result">
                    {test.data.sent > 0
                      ? t('thisDevice.testSent', { count: test.data.sent })
                      : t('thisDevice.testNone')}
                    {test.data.removed > 0
                      ? ` ${t('thisDevice.testRemoved', { count: test.data.removed })}`
                      : null}
                  </p>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  );
}
